<template>
  <div>
    <div class="toolbar">
      <h2>广告管理</h2>
    </div>

    <el-card class="section" shadow="never">
      <div slot="header">立即广告</div>
      <el-form inline>
        <el-form-item label="选择商家">
          <el-select v-model="immediateMerchantId" filterable placeholder="选择商家" style="width:240px">
            <el-option
              v-for="m in merchants"
              :key="m.id"
              :label="`${m.name_zh} (ID ${m.id})`"
              :value="m.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="danger" :loading="broadcasting" @click="onImmediateBroadcast">
            立即广告
          </el-button>
        </el-form-item>
      </el-form>
      <div class="tip">将向 users 表全部用户推送该商家；遵守 Telegram 限速，请保持机器人进程（npm run dev 或 server）运行</div>
    </el-card>

    <el-card class="section" shadow="never">
      <div slot="header">自动广告（每日定时）</div>
      <el-form label-width="90px">
        <el-form-item label="选择商家">
          <el-select v-model="scheduleMerchantId" filterable placeholder="选择商家" style="width:100%">
            <el-option
              v-for="m in merchants"
              :key="m.id"
              :label="`${m.name_zh} (ID ${m.id})`"
              :value="m.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="发送时间">
          <div v-for="(time, index) in scheduleTimes" :key="index" class="time-row">
            <el-time-select
              v-model="scheduleTimes[index]"
              :picker-options="timePickerOptions"
              placeholder="选择时间"
              style="width:160px"
            />
            <el-button
              type="danger"
              icon="el-icon-delete"
              circle
              plain
              :disabled="scheduleTimes.length <= 1"
              @click="removeTime(index)"
            />
          </div>
          <el-button type="text" icon="el-icon-plus" @click="addTime">添加时间</el-button>
          <div class="tip">24 小时内可设置多个时刻，到点自动发送（需机器人运行）</div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="onCreateSchedule">设置自动广告</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="section" shadow="never">
      <div slot="header">进行中的自动广告</div>
      <div v-if="!isMobile" class="table-scroll">
      <el-table :data="activeSchedules" v-loading="loadingSchedules" border empty-text="暂无自动广告">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="merchant_name_zh" label="商家" />
        <el-table-column label="每日时间">
          <template slot-scope="{ row }">{{ formatTimes(row.times) }}</template>
        </el-table-column>
        <el-table-column prop="admin_username" label="创建人" width="100" />
        <el-table-column label="操作" width="100">
          <template slot-scope="{ row }">
            <el-button type="text" style="color:#f56c6c" @click="onCancelSchedule(row)">取消</el-button>
          </template>
        </el-table-column>
      </el-table>
      </div>
      <MobileCardList
        v-if="isMobile"
        :data="activeSchedules"
        :loading="loadingSchedules"
        :fields="scheduleCardFields"
        empty-text="暂无自动广告"
      >
        <template #times="{ row }">{{ formatTimes(row.times) }}</template>
        <template #actions="{ row }">
          <el-button type="text" style="color:#f56c6c" @click="onCancelSchedule(row)">取消</el-button>
        </template>
      </MobileCardList>
    </el-card>

    <el-card class="section" shadow="never">
      <div slot="header">发送记录</div>
      <div v-if="!isMobile" class="table-scroll">
      <el-table :data="broadcasts" v-loading="loadingBroadcasts" border>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="merchant_name_zh" label="商家" />
        <el-table-column label="类型" width="90">
          <template slot-scope="{ row }">
            {{ row.type === 'immediate' ? '立即' : '定时' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="statusTag(row.status)" size="mini">{{ statusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="total_users" label="目标" width="70" />
        <el-table-column prop="success_count" label="成功" width="70" />
        <el-table-column prop="fail_count" label="失败" width="70" />
        <el-table-column prop="admin_username" label="操作人" width="100" />
        <el-table-column label="时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
      </el-table>
      </div>
      <MobileCardList
        v-if="isMobile"
        :data="broadcasts"
        :loading="loadingBroadcasts"
        :fields="broadcastCardFields"
      >
        <template #type="{ row }">{{ row.type === 'immediate' ? '立即' : '定时' }}</template>
        <template #status="{ row }">
          <el-tag :type="statusTag(row.status)" size="mini">{{ statusText(row.status) }}</el-tag>
        </template>
        <template #created_at="{ row }">{{ formatTime(row.created_at) }}</template>
      </MobileCardList>
    </el-card>
  </div>
</template>

<script>
import { api } from '../api';
import responsiveMixin from '../mixins/responsive';

export default {
  name: 'Ad',
  mixins: [responsiveMixin],
  data() {
    return {
      merchants: [],
      broadcasts: [],
      schedules: [],
      immediateMerchantId: null,
      scheduleMerchantId: null,
      scheduleTimes: ['09:00'],
      broadcasting: false,
      pollTimer: null,
      loadingBroadcasts: false,
      loadingSchedules: false,
      timePickerOptions: {
        start: '00:00',
        step: '00:30',
        end: '23:30',
      },
      scheduleCardFields: [
        { label: 'ID', prop: 'id' },
        { label: '商家', prop: 'merchant_name_zh' },
        { label: '每日时间', prop: 'times' },
        { label: '创建人', prop: 'admin_username' },
      ],
      broadcastCardFields: [
        { label: 'ID', prop: 'id' },
        { label: '商家', prop: 'merchant_name_zh' },
        { label: '类型', prop: 'type' },
        { label: '状态', prop: 'status' },
        { label: '目标', prop: 'total_users' },
        { label: '成功', prop: 'success_count' },
        { label: '失败', prop: 'fail_count' },
        { label: '操作人', prop: 'admin_username' },
        { label: '时间', prop: 'created_at' },
      ],
    };
  },
  computed: {
    activeSchedules() {
      return this.schedules.filter((s) => s.status === 'active');
    },
  },
  created() {
    this.loadMerchants();
    this.loadBroadcasts();
    this.loadSchedules();
  },
  beforeDestroy() {
    this.stopPoll();
  },
  methods: {
    async loadMerchants() {
      try {
        const { data } = await api.merchants.list();
        this.merchants = data.filter((m) => m.is_active);
      } catch (e) {
        this.$message.error(e.message);
      }
    },
    async loadBroadcasts() {
      this.loadingBroadcasts = true;
      try {
        const { data } = await api.ads.broadcasts();
        this.broadcasts = data;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.loadingBroadcasts = false;
      }
    },
    async loadSchedules() {
      this.loadingSchedules = true;
      try {
        const { data } = await api.ads.schedules();
        this.schedules = data;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.loadingSchedules = false;
      }
    },
    addTime() {
      this.scheduleTimes.push('12:00');
    },
    removeTime(index) {
      if (this.scheduleTimes.length <= 1) return;
      this.scheduleTimes.splice(index, 1);
    },
    formatTimes(times) {
      const list = typeof times === 'string' ? JSON.parse(times) : times;
      return Array.isArray(list) ? list.join('、') : '';
    },
    formatTime(val) {
      if (!val) return '';
      return String(val).replace('T', ' ').slice(0, 19);
    },
    statusText(status) {
      const map = {
        pending: '等待',
        running: '发送中',
        completed: '完成',
        failed: '失败',
      };
      return map[status] || status;
    },
    statusTag(status) {
      if (status === 'completed') return 'success';
      if (status === 'failed') return 'danger';
      if (status === 'running') return 'warning';
      return 'info';
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    startPoll(broadcastId) {
      this.stopPoll();
      this.pollTimer = setInterval(async () => {
        try {
          const { data } = await api.ads.broadcastStatus(broadcastId);
          const idx = this.broadcasts.findIndex((b) => b.id === broadcastId);
          if (idx >= 0) {
            this.$set(this.broadcasts, idx, data);
          } else {
            this.broadcasts.unshift(data);
          }
          if (data.status === 'completed' || data.status === 'failed') {
            this.stopPoll();
            this.broadcasting = false;
            this.$message({
              type: data.status === 'completed' ? 'success' : 'error',
              message: `发送结束：成功 ${data.success_count}，失败 ${data.fail_count}`,
              duration: 5000,
            });
            this.loadBroadcasts();
          }
        } catch (e) {
          this.stopPoll();
          this.broadcasting = false;
        }
      }, 2000);
    },
    onImmediateBroadcast() {
      if (!this.immediateMerchantId) {
        this.$message.warning('请选择商家');
        return;
      }
      const name = this.merchants.find((m) => m.id === this.immediateMerchantId)?.name_zh;
      this.$confirm(`确认立即向所有用户推送「${name}」？`, '立即广告', { type: 'warning' })
        .then(async () => {
          this.broadcasting = true;
          try {
            const { data } = await api.ads.broadcast(this.immediateMerchantId);
            this.$message.success('已开始发送，请稍候…');
            await this.loadBroadcasts();
            this.startPoll(data.id);
          } catch (e) {
            this.broadcasting = false;
            this.$message.error(e.message);
          }
        })
        .catch(() => {});
    },
    onCreateSchedule() {
      if (!this.scheduleMerchantId) {
        this.$message.warning('请选择商家');
        return;
      }
      const times = this.scheduleTimes.filter(Boolean);
      if (!times.length) {
        this.$message.warning('请至少设置一个时间');
        return;
      }
      const name = this.merchants.find((m) => m.id === this.scheduleMerchantId)?.name_zh;
      this.$confirm(
        `确认为「${name}」设置每日 ${times.join('、')} 自动广告？`,
        '自动广告',
        { type: 'warning' }
      )
        .then(async () => {
          try {
            await api.ads.createSchedule(this.scheduleMerchantId, times);
            this.$message.success('自动广告已设置');
            this.loadSchedules();
          } catch (e) {
            this.$message.error(e.message);
          }
        })
        .catch(() => {});
    },
    onCancelSchedule(row) {
      this.$confirm(`确认取消「${row.merchant_name_zh}」的自动广告？`, '取消确认', {
        type: 'warning',
      })
        .then(async () => {
          try {
            await api.ads.cancelSchedule(row.id);
            this.$message.success('已取消');
            this.loadSchedules();
          } catch (e) {
            this.$message.error(e.message);
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.toolbar { margin-bottom: 16px; }
.toolbar h2 { margin: 0; }
.section { margin-bottom: 20px; }
.time-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.tip { font-size: 12px; color: #909399; margin-top: 4px; }
</style>
